import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero } from "../../components/Bits";

const CATEGORIES = ["General", "Support Request", "Volunteer", "Donation", "Certificate", "Partnership", "Media", "Other"];

export default function Contact() {
  const { t, site } = useApp();
  const s = site?.settings || {};
  const [form, setForm] = useState({ category: "General", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please give consent to be contacted."); return; }
    setBusy(true);
    try {
      const r = await api.post("/public/enquiries", form);
      setDone(r.data);
      toast.success("Enquiry submitted");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const waText = encodeURIComponent("Namaste, I would like to connect with Sahaj Sarathi Foundation.");

  return (
    <div data-testid="contact-page">
      <SEO title={t("contact")} description="Contact Sahaj Sarathi Foundation — phone, email, WhatsApp or the online enquiry form." />
      <PageHero title={t("contact")} sub={s.tagline_en} />
      <section className="py-16">
        <div className="container-ssf grid gap-10 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="card-ssf p-8">
              <h2 className="font-display text-2xl font-bold text-navy">Sahaj Sarathi Foundation</h2>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 shrink-0 text-saffron-600" /><span>{s.address_line}</span></li>
                <li><a href={`tel:${(s.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-3 hover:text-saffron-600" data-testid="contact-phone"><Phone size={18} className="shrink-0 text-saffron-600" />{s.phone}</a></li>
                <li><a href={`mailto:${s.email}`} className="flex items-center gap-3 hover:text-saffron-600" data-testid="contact-email"><Mail size={18} className="shrink-0 text-saffron-600" />{s.email}</a></li>
                <li><a href={`https://wa.me/${s.whatsapp}?text=${waText}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-saffron-600" data-testid="contact-whatsapp"><MessageCircle size={18} className="shrink-0 text-saffron-600" />WhatsApp — click to chat</a></li>
              </ul>
              <div className="mt-6 border-t border-gray-100 pt-5 text-xs text-slate-500">
                <p>{t("office")}: {s.city}, {s.state} — {s.pin}</p>
                <p className="mt-1">UDYAM: {s.udyam} | NGO DARPAN: {s.darpan_id}</p>
              </div>
            </div>
            {s.map_embed_url && (
              <div className="card-ssf overflow-hidden">
                <iframe title="Sahaj Sarathi Foundation location map" src={s.map_embed_url} className="h-72 w-full border-0" loading="lazy" data-testid="contact-map" />
              </div>
            )}
          </div>

          <div>
            {done ? (
              <div className="card-ssf p-10 text-center" data-testid="enquiry-success">
                <CheckCircle2 className="mx-auto h-14 w-14 text-brandgreen" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-bold text-navy">Thank you!</h2>
                <p className="mt-2 text-sm text-slate-600">Your enquiry has been received. Reference number:</p>
                <p className="mt-1 font-display text-3xl font-bold text-saffron-600" data-testid="enquiry-ref">{done.ref_no}</p>
                {!done.email_sent && <p className="mt-4 text-xs text-slate-500">Email notification not configured — please note your reference number.</p>}
              </div>
            ) : (
              <form onSubmit={submit} className="card-ssf space-y-5 p-8" data-testid="contact-form">
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-sm font-semibold text-navy">{t("full_name")} *</label>
                  <input id="cf-name" data-testid="cf-name" required value={form.name || ""} onChange={set("name")} className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-mobile" className="mb-1.5 block text-sm font-semibold text-navy">{t("mobile")}</label>
                    <input id="cf-mobile" data-testid="cf-mobile" type="tel" value={form.mobile || ""} onChange={set("mobile")} className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-1.5 block text-sm font-semibold text-navy">{t("email")}</label>
                    <input id="cf-email" data-testid="cf-email" type="email" value={form.email || ""} onChange={set("email")} className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-category" className="mb-1.5 block text-sm font-semibold text-navy">{t("category")}</label>
                  <select id="cf-category" data-testid="cf-category" value={form.category} onChange={set("category")} className="w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-message" className="mb-1.5 block text-sm font-semibold text-navy">{t("message")} *</label>
                  <textarea id="cf-message" data-testid="cf-message" required rows={5} value={form.message || ""} onChange={set("message")} className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
                </div>
                <label className="flex items-start gap-2.5 text-sm text-slate-600">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 rounded accent-saffron-500" data-testid="cf-consent" />
                  {t("consent_text")}
                </label>
                <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="contact-submit">
                  {busy ? t("loading") : t("send_message")}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
